/**
 * SyncStatusIndicator Component
 * 
 * Feature 10: Visible offline indicator and sync status
 * 
 * Shows a banner/indicator when:
 * - User is offline (shows warning banner)
 * - Data is syncing (shows syncing indicator)
 * - Data is synced (subtle checkmark or hidden)
 * 
 * Uses useSyncStatus hook for status detection
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  WifiOff, 
  Cloud, 
  CloudOff, 
  CheckCircle, 
  RefreshCw,
  AlertTriangle
} from 'lucide-react';
import { useSyncStatus } from '@/hooks/useSyncStatus';

/**
 * SyncStatusBanner Component
 * Full-width banner that appears at top of screen when offline or syncing
 */
export function SyncStatusBanner() {
  const syncStatus = useSyncStatus();
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showBanner, setShowBanner] = useState(false);
  const [recentlySynced, setRecentlySynced] = useState(false);

  // Monitor online/offline events directly
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Show banner based on status
  useEffect(() => {
    if (!isOnline || syncStatus === 'local') {
      setShowBanner(true);
      setRecentlySynced(false);
    } else if (syncStatus === 'syncing') {
      setShowBanner(true);
      setRecentlySynced(false);
    } else if (syncStatus === 'synced' && showBanner) {
      // Show "synced" briefly then hide
      setRecentlySynced(true);
      const timer = setTimeout(() => {
        setShowBanner(false);
        setRecentlySynced(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [syncStatus, isOnline, showBanner]);

  return (
    <AnimatePresence>
      {showBanner && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="overflow-hidden"
        >
          <div
            className={`px-4 py-2 text-center text-sm font-medium flex items-center justify-center gap-2
              ${!isOnline 
                ? 'bg-amber-500 text-white' 
                : recentlySynced 
                  ? 'bg-green-500 text-white'
                  : syncStatus === 'local'
                    ? 'bg-amber-100 dark:bg-amber-900/50 text-amber-800 dark:text-amber-200'
                    : 'bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-200'
              }
            `}
          >
            {!isOnline ? (
              <>
                <WifiOff className="w-4 h-4" />
                <span>You're offline. Changes will sync when you're back online.</span>
              </>
            ) : recentlySynced ? (
              <>
                <CheckCircle className="w-4 h-4" />
                <span>All changes synced!</span>
              </>
            ) : syncStatus === 'local' ? (
              <>
                <CloudOff className="w-4 h-4" />
                <span>Working from local cache. Reconnecting...</span>
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                <span>Syncing changes...</span>
              </>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/**
 * SyncStatusIcon Component
 * Compact icon indicator for header/nav bar
 */
export function SyncStatusIcon({ className = '' }) {
  const syncStatus = useSyncStatus();
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Only show icon when not synced
  if (isOnline && syncStatus === 'synced') {
    return (
      <div className={`text-green-500 ${className}`} title="All changes synced">
        <Cloud className="w-4 h-4" />
      </div>
    );
  }

  if (!isOnline) {
    return (
      <div className={`text-amber-500 ${className}`} title="You're offline">
        <WifiOff className="w-4 h-4" />
      </div>
    );
  }

  if (syncStatus === 'local') {
    return (
      <div className={`text-amber-500 ${className}`} title="Working from local cache">
        <CloudOff className="w-4 h-4" />
      </div>
    );
  }

  // Syncing
  return (
    <div className={`text-blue-500 ${className}`} title="Syncing...">
      <RefreshCw className="w-4 h-4 animate-spin" />
    </div>
  );
}

/**
 * OfflineQueueIndicator Component
 * Shows count of pending changes when offline
 */
export function OfflineQueueIndicator({ pendingCount = 0 }) {
  const syncStatus = useSyncStatus();
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Only show when offline with pending changes
  if (isOnline || pendingCount === 0) {
    return null;
  }

  return (
    <motion.div
      initial={{ scale: 0.8, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      className="fixed bottom-20 right-4 bg-amber-500 text-white rounded-full px-4 py-2 shadow-lg flex items-center gap-2 z-50"
    >
      <AlertTriangle className="w-4 h-4" />
      <span className="text-sm font-medium">
        {pendingCount} pending {pendingCount === 1 ? 'change' : 'changes'}
      </span>
    </motion.div>
  );
}

/**
 * Main SyncStatusIndicator export
 * Combines banner and icon for easy import
 */
export default function SyncStatusIndicator({ 
  variant = 'banner', // 'banner' | 'icon' | 'both'
  className = '' 
}) {
  if (variant === 'icon') {
    return <SyncStatusIcon className={className} />;
  }
  
  if (variant === 'both') {
    return (
      <>
        <SyncStatusBanner />
        <SyncStatusIcon className={className} />
      </>
    );
  }
  
  return <SyncStatusBanner />;
}
